/**
 * @fileoverview Shared contact-form state hook used by ContactInlineForm.
 * @module ui-admin/components/shared-forms
 *
 * Responsibilities:
 * - Maintain ContactFormValues local state with field-level updates.
 * - Run synchronous Zod validation on submit.
 * - Run async per-domain MX validation against email rows before final submit.
 *
 * Dependencies:
 * - react (peer)
 * - @firesquirrel/core-domain (contactFormSchema + validationHelpers)
 *
 * Architecture:
 * - Pure host-neutral hook; no Dexie or service access.
 *
 * Notes:
 * - Promoted from web/tenant-web/src/components/shared-forms/useContactForm.ts
 *   to enable reuse from tenant-web, system-web, and mobile-pwa.
 */

import { useState, useCallback } from 'react';
import type { ContactFormValues, ContactRole } from '@firesquirrel/core-domain';
import { contactValidationSchema, computeContactDefaults } from '@firesquirrel/core-domain';
import { validateEmailMx } from '@firesquirrel/core-domain';

// Module-level MX domain cache — persists across form instances for the session
const mxDomainCache = new Map<string, string | null>();

export interface UseContactFormOptions {
  prefillName?: string;
}

/**
 * @hook useContactForm
 *
 * Purpose:
 * - Encapsulates ContactFormValues state, sync + async validation.
 *
 * Returns:
 * - form, errors, emailAsyncErrors, formError, isSubmitting,
 *   updateField, validate, validateAndSubmit, setForm, setErrors, reset.
 *
 * Side Effects:
 * - validateAndSubmit runs validateEmailMx() against unique email domains.
 *
 * Offline Behavior:
 * - validateEmailMx degrades gracefully on network failure; the form still
 *   submits when MX cannot be reached.
 */
export function useContactForm(opts?: UseContactFormOptions) {
  const [form, setForm] = useState<ContactFormValues>(() =>
    computeContactDefaults(opts?.prefillName)
  );

  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormValues, string>>>({});
  const [emailAsyncErrors, setEmailAsyncErrors] = useState<Map<string, string>>(new Map());
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = useCallback(<K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'firstName' || key === 'lastName') {
        const fn = (key === 'firstName' ? String(value ?? '') : String(prev.firstName ?? '')).trim();
        const ln = (key === 'lastName' ? String(value ?? '') : String(prev.lastName ?? '')).trim();
        next.name = [fn, ln].filter(Boolean).join(' ');
      }
      return next;
    });
    setFormError(null);
    setErrors(prev => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return prev;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const cleaned = {
      ...form,
      phoneNumbers: form.phoneNumbers.filter(p => p.number.trim() !== ''),
      emails: form.emails.filter(e => e.email.trim() !== ''),
    };
    const result = contactValidationSchema.safeParse(cleaned);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormValues, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof ContactFormValues;
        if (!fieldErrors[key]) {
          fieldErrors[key] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  }, [form]);

  const validateAndSubmit = useCallback(async (onSubmit: (values: ContactFormValues) => void | Promise<void>) => {
    // 1. Strip empty placeholder entries before validation — the form defaults
    // include one empty phone and one empty email for UX convenience; when the
    // user leaves them blank, strip them so the optional-array Zod schema sees
    // an empty array (valid) rather than an entry with empty required fields.
    const cleaned = {
      ...form,
      phoneNumbers: form.phoneNumbers.filter(p => p.number.trim() !== ''),
      emails: form.emails.filter(e => e.email.trim() !== ''),
    };

    // 1. Synchronous Zod validation
    const result = contactValidationSchema.safeParse(cleaned);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormValues, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof ContactFormValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    // 2. Async MX validation on each unique email domain
    setIsSubmitting(true);
    setEmailAsyncErrors(new Map());
    setFormError(null);
    try {
      // MX-check the parsed list — schema parsing already stripped empty
      // placeholder entries and applied coercions/defaults.
      const uniqueDomains = new Map<string, string[]>(); // domain → [email _id, ...]
      for (const em of result.data.emails) {
        if (!em.email.includes('@')) continue;
        const domain = em.email.split('@')[1].toLowerCase();
        const ids = uniqueDomains.get(domain) ?? [];
        ids.push(em._id);
        uniqueDomains.set(domain, ids);
      }

      const newAsyncErrors = new Map<string, string>();
      for (const [domain, ids] of uniqueDomains) {
        let mxResult: string | null;
        if (mxDomainCache.has(domain)) {
          mxResult = mxDomainCache.get(domain)!;
        } else {
          mxResult = await validateEmailMx(`check@${domain}`);
          mxDomainCache.set(domain, mxResult);
        }
        if (mxResult) {
          for (const id of ids) newAsyncErrors.set(id, mxResult);
        }
      }

      if (newAsyncErrors.size > 0) {
        setEmailAsyncErrors(newAsyncErrors);
        setFormError('Fix the highlighted email address issues before saving.');
        setIsSubmitting(false);
        return;
      }
    } catch {
      // MX network failure is non-blocking — fall through and submit anyway.
    }

    // 3. Submit exactly once, and AWAIT it so isSubmitting (which disables the
    // submit button) stays true through the host's async persistence
    // (e.g. Dexie writes in onCreate). This prevents a double-submit where a
    // second click during the await would create a duplicate contact. The
    // single call site here also guarantees onSubmit never fires twice, even
    // when MX validation throws above.
    // A rejected onSubmit (validation thrown by the host's write service, Dexie
    // failure, missing contact) is surfaced as formError so the user sees why
    // the save did not complete instead of the form silently closing.
    // Submit result.data — the PARSED values — so schema coercions and defaults
    // (postalCode number→string, validationStatus/countryCode defaults, trimmed
    // name, cleared-address null sentinel) actually reach the save callback;
    // submitting `cleaned` would discard every normalization the schema applied.
    try {
      await onSubmit(result.data);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save contact.');
    } finally {
      setIsSubmitting(false);
    }
  }, [form]);

  const reset = useCallback((prefillName?: string) => {
    setForm(computeContactDefaults(prefillName));
    setErrors({});
    setEmailAsyncErrors(new Map());
    setFormError(null);
  }, []);

  return { form, errors, emailAsyncErrors, formError, isSubmitting, updateField, validate, validateAndSubmit, setForm, setErrors, reset };
}

export type { ContactFormValues, ContactRole };
export type { PhoneEntry, EmailEntry, PhoneType, EmailType } from '@firesquirrel/core-domain';
